import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Menu,
  MenuItem,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format, startOfMonth } from "date-fns";
import html2pdf from "html2pdf.js";
import { generateReport } from "../services/Report/service";
import { Report, SessionReport } from "../types/models";
import { fetchSessionReport } from "../services/UserSession/service";

type Props = {
  open: boolean;
  onClose: () => void;
  accountRootId: number;
  accountRootName: string;
};

const ReportDialog: React.FC<Props> = ({
  open,
  onClose,
  accountRootId,
  accountRootName,
}) => {
  const [billingMonth, setBillingMonth] = useState<Date | null>(new Date());
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [sessionReport, setSessionReport] = useState<SessionReport[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [sessionsError, setSessionsError] = useState<string | null>(null);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const reportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setReport(null);
      setSessionReport([]);
      setBillingMonth(new Date());
      setError(null);
      setSessionsError(null);
    }
  }, [open, accountRootId]);

  const handleGenerate = async () => {
    if (!billingMonth) return;

    const monthKey = format(startOfMonth(billingMonth), "yyyy-MM");
    setLoading(true);
    setError(null);
    try {
      const rep = await generateReport({
        account_root_id: accountRootId,
        billing_month: monthKey,
      });
      setReport(rep);
    } catch (err) {
      console.error(err);
      setError("Failed to generate totals report.");
    } finally {
      setLoading(false);
    }

    setLoadingSessions(true);
    setSessionsError(null);
    try {
      const sess = await fetchSessionReport(accountRootId, monthKey);
      setSessionReport(sess);
    } catch (err) {
      console.error(err);
      setSessionsError("Failed to fetch session details.");
    } finally {
      setLoadingSessions(false);
    }
  };

  const handleExportClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
  };
  const handleExportClose = () => setAnchorEl(null);

  const exportAsPdf = async () => {
    handleExportClose();
    if (!reportRef.current || !billingMonth) return;
    await new Promise((r) => setTimeout(r, 50));

    html2pdf()
      .set({
        margin: 0.5,
        filename: `report-${accountRootName}-${format(
          startOfMonth(billingMonth!),
          "yyyy-MM"
        )}.pdf`,
        jsPDF: { unit: "in", format: "letter", orientation: "landscape" },
        html2canvas: {
          backgroundColor: "#161B22",
          useCORS: true,
          scale: 2,
        },
      })
      .from(reportRef.current)
      .save();
  };

  const exportAsCsv = () => {
    if (!sessionReport.length || !billingMonth) return;

    const monthKey = format(startOfMonth(billingMonth), "yyyy-MM");
    const rows: string[][] = [];
    rows.push([
      "User",
      "Session Start",
      "Scenarios",
      "Payable (min)",
      "Downtime (min)",
      "Total Time (min)",
    ]);
    sessionReport.forEach((u) => {
      u.sessions.forEach((s) => {
        const startFmt = format(new Date(s.start_time), "yyyy-MM-dd HH:mm");
        const payableMin = Math.ceil(s.payable_time / 60).toString();
        const downtimeMin = Math.ceil(s.downtime / 60).toString();
        const totalMin = Math.ceil((s.payable_time + s.downtime) / 60).toString();

        rows.push([
          u.username,
          startFmt,
          s.scenario_count.toString(),
          payableMin,
          downtimeMin,
          totalMin,
        ]);
      });
    });


    const csvContent = rows
      .map((r) =>
        r.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(",")
      )
      .join("\r\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `report-${accountRootName}-${monthKey}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ backgroundColor: "#161B22", color: "#fff" }}>
        Generate Billing Report
      </DialogTitle>

      <DialogContent sx={{ backgroundColor: "#161B22" }}>
        <DatePicker
          label="Billing Month"
          views={["year", "month"]}
          value={billingMonth}
          onChange={setBillingMonth}
          slotProps={{
            textField: {
              fullWidth: true,
              sx: {
                mt: 2,
                input: { color: "#fff" },
                label: { color: "#A8B2D1" },
                "& .MuiSvgIcon-root": { color: "#A8A8A8" },
              },
            },
          }}
        />

        <Box sx={{ mt: 2 }}>
          <Button
            onClick={handleGenerate}
            variant="contained"
            sx={{ backgroundColor: "#2ea043" }}
            disabled={loading || loadingSessions}
          >
            {loading || loadingSessions ? (
              <CircularProgress size={20} />
            ) : (
              "Generate"
            )}
          </Button>
        </Box>
        {error && (
          <Typography color="error" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}

        {report && (
          <Box
            ref={reportRef}
            sx={{
              mt: 4,
              p: 2,
              backgroundColor: "#161B22",
              color: "#fff",
              backgroundImage: `url("/logo.png")`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "top right",
              backgroundSize: "120px auto",
            }}
          >
            <Typography variant="h6">Account: {accountRootName}</Typography>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Billing: {format(startOfMonth(billingMonth!), "MMMM yyyy")}
            </Typography>

            <Typography variant="h6" sx={{ mt: 3 }}>
              Sessions Report
            </Typography>

            {loadingSessions ? (
              <CircularProgress sx={{ mt: 2 }} />
            ) : sessionsError ? (
              <Typography color="error" sx={{ mt: 2 }}>
                {sessionsError}
              </Typography>
            ) : (
              <TableContainer
                component={Paper}
                sx={{
                  mt: 2,
                  backgroundColor: "#1F2937",
                  minWidth: 900,
                  "& thead": {
                    display: "table-header-group",
                  },
                  "& tr": {
                    pageBreakInside: "avoid",
                    breakInside: "avoid",
                  },
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: "#A8B2D1" }}>User</TableCell>
                      <TableCell sx={{ color: "#A8B2D1" }}>
                        Session Start
                      </TableCell>
                      <TableCell sx={{ color: "#A8B2D1" }}>Scenarios</TableCell>
                      <TableCell sx={{ color: "#A8B2D1" }}>
                        Training Time (min)
                      </TableCell>
                      <TableCell sx={{ color: "#A8B2D1" }}>
                        Idle Time (min)
                      </TableCell>
                      <TableCell sx={{ color: "#A8B2D1" }}>
                        Total Time (min)
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sessionReport.map((u) => {
                      const userTotals = u.sessions.reduce(
                        (acc, s) => {
                          acc.scenarios += s.scenario_count;
                          acc.payable += s.payable_time;
                          acc.downtime += s.downtime;
                          return acc;
                        },
                        { scenarios: 0, payable: 0, downtime: 0 }
                      );

                      return (
                        <React.Fragment key={u.user_id}>
                          {u.sessions.map((s, idx) => {
                            const startFmt = format(
                              new Date(s.start_time),
                              "yyyy-MM-dd HH:mm"
                            );
                            const payMin = Math.ceil(s.payable_time / 60);
                            const downMin = Math.ceil(s.downtime / 60);
                            const totMin = Math.ceil((s.payable_time + s.downtime) / 60);

                            return (
                              <TableRow key={`${u.user_id}-${s.start_time}`}>
                                {idx === 0 && (
                                  <TableCell
                                    rowSpan={
                                      u.sessions.length > 1
                                        ? u.sessions.length + 1
                                        : 1
                                    }
                                    sx={{
                                      color: "#fff",
                                      verticalAlign: "top",
                                    }}
                                  >
                                    {u.username}
                                  </TableCell>
                                )}
                                <TableCell sx={{ color: "#fff" }}>{startFmt}</TableCell>
                                <TableCell sx={{ color: "#fff" }}>{s.scenario_count}</TableCell>
                                <TableCell sx={{ color: "#fff" }}>{payMin}</TableCell>
                                <TableCell sx={{ color: "#fff" }}>{downMin}</TableCell>
                                <TableCell sx={{ color: "#fff" }}>{totMin}</TableCell>
                              </TableRow>
                            );
                          })}
                          {u.sessions.length > 1 && (
                            <TableRow sx={{ backgroundColor: "#374151" }}>
                              <TableCell sx={{ color: "#CBD5E1", fontWeight: "bold" }}>
                                User Total:
                              </TableCell>
                              <TableCell sx={{ color: "#CBD5E1", fontWeight: "bold" }}>
                                {/* empty cell for spacing */}
                              </TableCell>
                              <TableCell sx={{ color: "#CBD5E1", fontWeight: "bold" }}>
                                {userTotals.scenarios}
                              </TableCell>
                              <TableCell sx={{ color: "#CBD5E1", fontWeight: "bold" }}>
                                {Math.ceil(userTotals.payable / 60)}
                              </TableCell>
                              <TableCell sx={{ color: "#CBD5E1", fontWeight: "bold" }}>
                                {Math.ceil(userTotals.downtime / 60)}
                              </TableCell>
                              <TableCell sx={{ color: "#CBD5E1", fontWeight: "bold" }}>
                                {Math.ceil((userTotals.payable + userTotals.downtime) / 60)}
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}

                    <TableRow sx={{ backgroundColor: "#161B22" }}>
                      <TableCell />
                      <TableCell sx={{ color: "#A8B2D1", fontWeight: "bold" }}>
                        Account Total:
                      </TableCell>
                      <TableCell sx={{ color: "#A8B2D1", fontWeight: "bold" }}>
                        {sessionReport.reduce(
                          (acc, u) =>
                            acc + u.sessions.reduce((a, s) => a + s.scenario_count, 0),
                          0
                        )}
                      </TableCell>
                      <TableCell sx={{ color: "#A8B2D1", fontWeight: "bold" }}>
                        {sessionReport.reduce(
                          (acc, u) =>
                            acc +
                            u.sessions.reduce((a, s) => a + Math.ceil(s.payable_time / 60), 0),
                          0
                        )}
                      </TableCell>
                      <TableCell sx={{ color: "#A8B2D1", fontWeight: "bold" }}>
                        {sessionReport.reduce(
                          (acc, u) =>
                            acc +
                            u.sessions.reduce((a, s) => a + Math.ceil(s.downtime / 60), 0),
                          0
                        )}
                      </TableCell>
                      <TableCell sx={{ color: "#A8B2D1", fontWeight: "bold" }}>
                        {sessionReport.reduce(
                          (acc, u) =>
                            acc +
                            u.sessions.reduce(
                              (a, s) =>
                                a + Math.ceil((s.payable_time + s.downtime) / 60),
                              0
                            ),
                          0
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ backgroundColor: "#161B22" }}>
        <Button onClick={onClose} sx={{ color: "#A8B2D1" }}>
          Close
        </Button>
        {report && (
          <>
            <Button
              onClick={handleExportClick}
              variant="outlined"
              size="small"
              endIcon={<span style={{ transform: "rotate(90deg)" }}>▶</span>}
              sx={{ color: "#A8B2D1", borderColor: "#A8B2D1" }}
            >
              Export
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleExportClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem onClick={exportAsPdf}>Export as PDF</MenuItem>
              <MenuItem onClick={exportAsCsv}>Export as CSV</MenuItem>
            </Menu>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReportDialog;
